import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { crmApi } from '@/api'
import { Lead } from '@/api/types/crm.types'

interface LeadRefetchContextType {
  handleRefetchLeadInModal: () => void
  handleRefetchLeadsOnClose: () => void
  state: {
    needRefetchLeads: boolean
    setNeedRefetchLeads: (value: boolean) => void
  }
}

const LeadRefetchContext = createContext<LeadRefetchContextType | undefined>(
  undefined,
)

interface LeadRefetchProviderProps {
  children: ReactNode
  leadId?: number
  setLead: (lead: Lead) => void
}

export const LeadRefetchProvider: React.FC<LeadRefetchProviderProps> = ({
  children,
  leadId,
  setLead,
}) => {
  const [needRefetchLeads, setNeedRefetchLeads] = useState(false)

  useEffect(() => {
    if (!leadId) setNeedRefetchLeads(false)
  }, [leadId])
  
  const handleRefetchLeadInModal = async () => {
    if (!leadId) return
    try {
      const res = await crmApi.getLead({ lead_id: leadId })
      if (res?.data?.data) setLead(res.data.data)
    } catch (error) {
      console.error('Failed to refetch lead:', error)
    }
  }

  const handleRefetchLeadsOnClose = () => {
    setNeedRefetchLeads(true)
  }

  const value: LeadRefetchContextType = {
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    state: {
      needRefetchLeads,
      setNeedRefetchLeads,
    },
  }

  return (
    <LeadRefetchContext.Provider value={value}>
      {children}
    </LeadRefetchContext.Provider>
  )
}

export const useLeadRefetch = (): LeadRefetchContextType => {
  const context = useContext(LeadRefetchContext)
  if (!context) {
    throw new Error('useLeadRefetch must be used within a LeadRefetchProvider')
  }
  return context
}
